export interface GpPatient {
  title: string;
  forename: string;
  middleName: string;
  surname: string;
  dob: string;
  sex: string;
  nhsNumber: string;
  address: string;
  phone: string;
  email: string;
  surgery: string;
}

export interface GpMedication {
  medicationId: string;
  drugName: string;
  dosage: string;
  dosageInstruction: string;
  quantity: string;
  status: string;
  prescriptionType: string;
  lastIssuedDate: string;
  reviewDate: string;
  prescribedByName: string;
  therapyLevel?: number | string | null;
}

export interface GpAllergy {
  allergyId: string;
  substanceName: string;
  criticality: string;
  verificationStatus: string;
  manifestationDescription: string;
  severity: string;
  category: string;
  note: string;
}

export interface GpImmunisation {
  immunisationId: string;
  vaccineName: string;
  doseNumber: number | null;
  administrationDate: string;
  site: string;
  route: string;
  batchNumber: string;
  administeredByName: string;
}

export interface GpProblem {
  problemId: string;
  problemName: string;
  clinicalStatus: string;
  significance: string;
  onsetDate: string;
  abatementDate: string;
  recordedBy: string;
  note: string;
}

export interface GpConsultationTopicEntry {
  entryId: string;
  entryType: string;
  display: string;
  value: string | null;
}

export interface GpConsultationTopic {
  topicId: string;
  title: string;
  entries: GpConsultationTopicEntry[];
}

export interface GpConsultation {
  consultationId: string;
  consultationDate: string;
  consultationTypeDisplay: string;
  practitionerName: string;
  practitionerRole: string;
  organisationName: string;
  topics: GpConsultationTopic[];
}

export interface GpObservation {
  observationId: string;
  observationName: string;
  effectiveDate: string;
  valueQuantity: string | null;
  valueUnit: string | null;
  valueString: string | null;
  interpretation: string | null;
  referenceRangeLow: string | null;
  referenceRangeHigh: string | null;
  note: string | null;
  performerName: string;
}

export interface GpReferral {
  referralId: string;
  status: string;
  priority: string;
  referralDescription: string;
  authoredDate: string;
  occurrenceDate: string | null;
  referrerName: string;
  recipientOrganisationName: string;
  recipientSpecialty: string;
  reasonCode: string;
  note: string;
}

export interface GpUncategorisedEntry {
  entryId: string;
  display: string;
  value: string;
  recordedDate: string;
  recordedBy: string;
  note: string | null;
}

export interface GpConnectRecord {
  patient: GpPatient;
  medications: GpMedication[];
  allergies: GpAllergy[];
  immunisations: GpImmunisation[];
  problems: GpProblem[];
  consultations: GpConsultation[];
  observations: GpObservation[];
  referrals: GpReferral[];
  uncategorisedData: GpUncategorisedEntry[];
}

export const emptyGpConnectRecord: GpConnectRecord = {
  patient: {
    title: "",
    forename: "",
    middleName: "",
    surname: "",
    dob: "",
    sex: "Unknown",
    nhsNumber: "",
    address: "",
    phone: "",
    email: "",
    surgery: "",
  },
  medications: [],
  allergies: [],
  immunisations: [],
  problems: [],
  consultations: [],
  observations: [],
  referrals: [],
  uncategorisedData: [],
};

export function formatGpDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function formatPatientSex(value: unknown): string {
  if (value === 1 || value === "1") return "Male";
  if (value === 2 || value === "2") return "Female";
  if (typeof value === "string" && value.trim()) return value;
  return "Unknown";
}

function getPreferredPhone(data: any): string {
  if (data?.preferredContact?.value) return data.preferredContact.value;

  const firstPhone = toArray<any>(data?.contacts).find(
    (contact) => contact?.contactType === 2 && contact?.value,
  );

  if (firstPhone?.value) return firstPhone.value;

  const firstContact = toArray<any>(data?.contacts).find(
    (contact) => contact?.value,
  );

  return firstContact?.value || "";
}

function getAddressString(data: any): string {
  const address = data?.preferredAddress?.address;
  if (!address) return "";

  return [
    address?.houseName,
    address?.streetName,
    address?.locality,
    address?.town,
    address?.county,
    address?.postcode,
    address?.addressLine1,
    address?.addressLine2,
  ]
    .filter(Boolean)
    .join(", ");
}

function getSurgeryDisplay(data: any): string {
  const surgery = data?.surgery;
  if (!surgery) return "";

  return (
    surgery?.name ||
    surgery?.surgeryName ||
    surgery?.displayName ||
    surgery?.organisationName ||
    ""
  );
}

export function mapAccessRecordToGpConnectRecord(rawData: any): GpConnectRecord {
  const payload = rawData?.response || rawData;

  if (!payload || typeof payload !== "object") {
    return emptyGpConnectRecord;
  }

  return {
    patient: {
      title: payload?.title || "",
      forename: payload?.forename || payload?.foreName || "",
      middleName: payload?.middleName || "",
      surname: payload?.surname || payload?.surName || "",
      dob: payload?.dob || "",
      sex: formatPatientSex(payload?.sex),
      nhsNumber: payload?.nhsNumber || payload?.meta?.patientNhsNumber || "",
      address: getAddressString(payload),
      phone: getPreferredPhone(payload),
      email:
        payload?.email ||
        payload?.preferredEmail ||
        payload?.emailAddress ||
        "",
      surgery: getSurgeryDisplay(payload),
    },
    medications: toArray<GpMedication>(payload?.medications),
    allergies: toArray<GpAllergy>(payload?.allergies),
    immunisations: toArray<GpImmunisation>(payload?.immunisations),
    problems: toArray<GpProblem>(payload?.problems),
    consultations: toArray<GpConsultation>(payload?.consultations),
    observations: toArray<GpObservation>(payload?.observations),
    referrals: toArray<GpReferral>(payload?.referrals),
    uncategorisedData: toArray<GpUncategorisedEntry>(payload?.uncategorisedData),
  };
}
